import { useEffect, useState } from 'react';

import { UnAuthAPIName } from 'src/constants';
import { GetVersionResponse } from 'src/entities/GetVersionResponse';
import { API } from 'src/utils/AmplifyApiUtils';
import { getCookie } from 'src/utils/StorageUtils';

const VERSION_POLL_INTERVAL = 5 * 60 * 1000; // poll every 5 minutes
const SUPPRESS_VERSION_CHECK_COOKIE = 'suppress-version-check';

// Module-level singleton state so polling runs once regardless of how many
// components call useTrackAppVersion().
let hasNewVersionGlobal = false;
let polling = false;
const listeners = new Set<(v: boolean) => void>();
const previousVersions: string[] = [];
let timer = 0;
let inFlight = false;

function notify() {
  listeners.forEach((fn) => fn(hasNewVersionGlobal));
}

async function trackVersion() {
  timer = 0;
  inFlight = true;
  try {
    const result: GetVersionResponse = await API.get(
      UnAuthAPIName,
      '/version',
      {},
    );

    console.info('version response: ', result);
    const { version } = result;
    if (!previousVersions.includes(version)) {
      previousVersions.push(version);
      // Length > 1 means this isn't the initial load — a new version deployed
      if (previousVersions.length > 1) {
        hasNewVersionGlobal = true;
        notify();
      }
    }
  } catch (ex) {
    console.error('error getting version', ex);
  }

  inFlight = false;
  scheduleNext();
}

// Guard against stopPolling() having been called while we were awaiting. A
// hidden tab skips scheduling entirely; onVisibilityChange resumes it, so a
// backgrounded or sleeping tab never holds a request open for hours.
function scheduleNext() {
  if (polling && !document.hidden) {
    timer = window.setTimeout(trackVersion, VERSION_POLL_INTERVAL);
  }
}

function onVisibilityChange() {
  if (!polling) return;
  if (document.hidden) {
    clearTimeout(timer);
    timer = 0;
  } else if (!timer && !inFlight) {
    trackVersion();
  }
}

function startPolling() {
  if (polling) return;
  if (getCookie(SUPPRESS_VERSION_CHECK_COOKIE)) return;
  polling = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  // Always record the baseline, even in a background tab, so a deploy that
  // lands before the tab is opened still registers as a new version.
  trackVersion();
}

function stopPolling() {
  if (timer) {
    clearTimeout(timer);
    timer = 0;
  }
  polling = false;
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

export const useTrackAppVersion = () => {
  const [hasNewVersion, setHasNewVersion] = useState(hasNewVersionGlobal);

  useEffect(() => {
    listeners.add(setHasNewVersion);
    startPolling();

    return () => {
      listeners.delete(setHasNewVersion);
      if (listeners.size === 0) {
        stopPolling();
      }
    };
  }, []);

  return { hasNewVersion };
};
